import type { ReactNode } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { Cpu, Zap } from 'lucide-react';
import { DEFAULT_POOL, SHARED_GPU_POOL, orgGpuPool } from '@vidmakr/shared';
import { useOrgPlan } from '@/lib/billing';
import { cx } from '@/components/ui';

export interface RenderTarget {
  id: string;
  icon: ReactNode;
  title: string;
  text: string;
  /** The plan it needs, when the organization's plan lacks it. */
  locked?: string;
  gpu?: boolean;
}

/** Where a render can run: the cloud CPU workers, the organization's own GPU workers, the shared GPU pool. */
export function useRenderTargets(orgId: string | undefined, pool: string) {
  const [workerPools] = useTable(tables.workerPools);
  const billing = useOrgPlan(orgId);
  const planSpec = billing?.enabled ? billing.limits.plan : null;
  const onlineIn = (p: string) => workerPools.find((w) => w.pool === p)?.online ?? 0;
  const ownPool = orgGpuPool(orgId ?? '');
  const ownOnline = onlineIn(ownPool);
  const sharedOnline = onlineIn(SHARED_GPU_POOL);
  const targets: RenderTarget[] = [
    { id: DEFAULT_POOL, icon: <Cpu size={16} />, title: 'Cloud (CPU)', text: onlineIn(DEFAULT_POOL) ? 'Always available' : 'No worker online right now' },
    {
      id: ownPool,
      icon: <Zap size={16} />,
      title: 'Our GPU workers',
      text: ownOnline ? `${ownOnline} online · NVENC, fastest for H.264/H.265/AV1` : 'None online · add one under GPU workers',
      locked: planSpec && !planSpec.ownWorkers && !ownOnline ? 'Team plan' : '',
      gpu: true,
    },
    ...(sharedOnline || pool === SHARED_GPU_POOL
      ? [{ id: SHARED_GPU_POOL, icon: <Zap size={16} />, title: 'Shared GPU', text: sharedOnline ? `${sharedOnline} online` : 'None online', locked: planSpec && !planSpec.gpu ? 'Team plan' : '', gpu: true }]
      : []),
  ];
  // Prefer the organization's own GPU workers when one is connected.
  const preferred = ownOnline ? ownPool : DEFAULT_POOL;
  return { targets, ownPool, ownOnline, onlineIn, preferred, billing, planSpec };
}

/** The "Render on" choice of exports and review renders. `gpuOk` is false for codecs NVENC cannot encode. */
export function RenderTargetPicker({
  targets,
  pool,
  onPool,
  gpuOk,
  className,
}: {
  targets: RenderTarget[];
  pool: string;
  onPool: (pool: string) => void;
  gpuOk: boolean;
  className?: string;
}) {
  return (
    <div className={className} data-render-target>
      <div className="mb-1.5 text-xs font-medium text-muted">Render on</div>
      <div className={cx('grid gap-2', targets.length > 2 ? 'grid-cols-3' : 'grid-cols-2')}>
        {targets.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onPool(o.id)}
            disabled={!!o.locked || (o.gpu && !gpuOk)}
            className={cx(
              'flex items-start gap-2 rounded-lg border p-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50',
              pool === o.id ? 'border-accent bg-accent/10' : 'border-line hover:bg-panel-2',
            )}
            data-render-target-option={o.id}
          >
            <span className="mt-0.5 text-accent-2">{o.icon}</span>
            <span>
              <span className="block text-sm font-medium">{o.title}</span>
              <span className="block text-xs text-muted">{o.locked ? `Part of the ${o.locked}` : o.gpu && !gpuOk ? 'H.264, H.265 and AV1 only' : o.text}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
